import React, { useState, useRef, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const sessionKey = "solair_chat_session";
const getSessionId = () => {
  let s = localStorage.getItem(sessionKey);
  if (!s) { s = crypto.randomUUID(); localStorage.setItem(sessionKey, s); }
  return s;
};

export const AIChat = () => {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.chat.welcome }]);
    }
  }, [open, t.chat.welcome, messages.length]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const token = localStorage.getItem("solair_token");
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: getSessionId(), message: userMsg, lang }),
      });
      if (res.status === 402) {
        const err = await res.json().catch(() => ({}));
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: `⚡ ${err.detail || "Limite giornaliero raggiunto"}. Passa a Pro per €2,50/mese per chat illimitata.` }; return c; });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const chunk = line.slice(6);
            if (chunk === "[DONE]") continue;
            if (chunk.startsWith("[ERROR]")) {
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: chunk }; return c; });
              continue;
            }
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + chunk };
              return c;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Error connecting to AI." }]);
    } finally { setSending(false); }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="ai-chat-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#17301C] text-[#EAB308] shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Open AI chat"
        >
          <Sparkles size={22} />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[min(400px,calc(100vw-2rem))] h-[540px] bg-white rounded-2xl shadow-2xl border border-[#DDE0D8] flex flex-col overflow-hidden" data-testid="ai-chat-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#DDE0D8] bg-[#17301C]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#EAB308] flex items-center justify-center">
                <Sparkles size={14} className="text-[#17301C]" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">SolarControl Minniti</div>
                <div className="text-xs text-white/70">{t.chat.title}</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-chat-close" className="text-white/80 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9F8F6]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                  m.role === "user"
                    ? "bg-[#17301C] text-white rounded-br-sm"
                    : "bg-white border border-[#DDE0D8] text-[#121A14] rounded-bl-sm"
                }`}>
                  {m.content || <span className="opacity-50">...</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-[#DDE0D8] bg-white">
            <div className="flex gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.chat.placeholder}
                className="flex-1 px-3 py-2 rounded-lg border border-[#DDE0D8] text-sm focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C]"
                disabled={sending}
              />
              <button
                data-testid="ai-chat-send"
                onClick={send}
                disabled={sending || !input.trim()}
                className="px-3 py-2 rounded-lg bg-[#EAB308] text-[#17301C] font-semibold transition-colors hover:bg-[#CA9A04] disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
